import { useCallback } from 'react';
import { supabase, Database } from './supabase';
import { getPublicUrl } from './storageHelpers';

export type AnimationOverview = Database['public']['Tables']['animation']['Row'] & {
  referenceImage?: string | null;
  animationVideo?: string | null;
  user_tag?: string | null;
};

export const useAnimateList = () => {
  /**
   * Lists animations across the platform that HAVE a video in their LATEST history entry.
   * Excludes hidden, deleted, and private items.
   */
  const listAllAnimations = useCallback(async (
    page: number = 1,
    pageSize: number = 8
  ): Promise<{ animations: AnimationOverview[]; hasMore: boolean }> => {
    try {
      const start = (page - 1) * pageSize;
      const end = start + pageSize - 1;

      // 1. Fetch records with the LATEST history entry only
      const { data: animData, error: animError, count } = await supabase
        .from('animation')
        .select(`
          *,
          animation_history_entries (
            animation_id, 
            reference_image, 
            animation_video, 
            updated_at
          )
        `, { count: 'exact' })
        .or('deleted.is.null,deleted.eq.false')
        .or('hidden.is.null,hidden.eq.false')
        .or('private.is.null,private.eq.false')
        // Sort the parent animations by recent update
        .order('updated_at', { ascending: false })
        // Sort the history entries so index [0] is the newest
        .order('updated_at', { foreignTable: 'animation_history_entries', ascending: false })
        // Only fetch the single most recent history record per animation
        .limit(1, { foreignTable: 'animation_history_entries' })
        .range(start, end);

      if (animError) throw animError;
      if (!animData || animData.length === 0) return { animations: [], hasMore: false };

      // 2. Transform and Filter
      const animations: AnimationOverview[] = animData
        .map(anim => {
          // Since we used limit(1), this array will have at most one entry: the latest
          const latest = (anim.animation_history_entries as any[])?.[0];
          
          // Only include if the LATEST entry specifically has a video
          if (!latest || !latest.animation_video) return null;

          let referenceImage = latest.reference_image || null;
          let animationVideo = latest.animation_video || null;

          if (referenceImage && !referenceImage.startsWith('http')) {
            referenceImage = getPublicUrl(supabase, referenceImage);
          }
          if (animationVideo && !animationVideo.startsWith('http')) {
            animationVideo = getPublicUrl(supabase, animationVideo);
          }

          const { animation_history_entries, ...rest } = anim;
          return { ...rest, referenceImage, animationVideo };
        })
        .filter((item): item is AnimationOverview => item !== null);

      return { animations, hasMore: !!count && count > end + 1 };
    } catch (error) {
      console.error('Error in listAllAnimations:', error);
      return { animations: [], hasMore: false };
    }
  }, []);

  /**
   * Lists only the SPECIFIC user's animations using the latest history for thumbnails.
   * Includes hidden items but excludes deleted items.
   */
  const listMyAnimations = useCallback(async (
    userId: string,
    page: number = 1,
    pageSize: number = 8
  ): Promise<{ animations: AnimationOverview[]; hasMore: boolean }> => {
    try {
      const start = (page - 1) * pageSize;
      const end = start + pageSize - 1;

      const { count, error: countError } = await supabase
        .from('animation')
        .select('id', { count: 'exact' })
        .eq('user_id', userId)
        .or('deleted.is.null,deleted.eq.false');
      
      if (countError) throw countError;
      if (!count || count === 0) return { animations: [], hasMore: false };

      const { data: animData, error: animError } = await supabase
        .from('animation')
        .select('*')
        .eq('user_id', userId)
        .or('deleted.is.null,deleted.eq.false')
        .order('updated_at', { ascending: false })
        .range(start, end);

      if (animError) throw animError;

      const animationIds = animData.map(a => a.id);
      
      // 3. Fetch history: sort by updated_at so the first encounter per ID is the latest
      const { data: historyData, error: historyError } = await supabase
        .from('animation_history_entries')
        .select('animation_id, reference_image, animation_video, updated_at')
        .in('animation_id', animationIds)
        .order('updated_at', { ascending: false });

      if (historyError) throw historyError;

      const historyMap = new Map();
      historyData?.forEach(entry => {
        // Because of the order('updated_at', descending), the first time we see 
        // an ID, it is the most recent entry
        if (!historyMap.has(entry.animation_id)) {
          historyMap.set(entry.animation_id, entry);
        }
      });

      // 4. Transform
      const animations: AnimationOverview[] = animData.map(anim => {
        const latest = historyMap.get(anim.id);
        let referenceImage = latest?.reference_image || null;
        let animationVideo = latest?.animation_video || null;

        if (referenceImage && !referenceImage.startsWith('http')) {
          referenceImage = getPublicUrl(supabase, referenceImage);
        }
        if (animationVideo && !animationVideo.startsWith('http')) {
          animationVideo = getPublicUrl(supabase, animationVideo);
        }

        return { ...anim, referenceImage, animationVideo };
      });

      return { animations, hasMore: count > end + 1 };
    } catch (error) {
      console.error('Error in listMyAnimations:', error);
      return { animations: [], hasMore: false };
    }
  }, []);

  return {
    listAllAnimations,
    listMyAnimations,
  };
};